import { useState } from 'react';
import {
  AppButton,
  AppChip,
  AppDateField,
  AppDivider,
  AppEmptyState,
  AppFormDialog,
  AppMenuSheet,
  AppPickerRow,
  AppScreenScaffold,
  AppSwipeRow,
  AppTextField,
  AppTopBar,
} from '@core/designsystem';
import { TaskRow } from './TaskRow';
import {
  isEmptyTab,
  TABS,
  type TaskCaseOption,
  type TaskListItem,
  type TasksState,
  type TasksTab,
} from './types';
import type { TasksActions } from './useTasks';
import './tasks.css';

/**
 * Экран «Задачи» — три вкладки и список с группами.
 *
 * Чистая функция состояния: ни базы, ни навигации внутри ([ARCHITECTURE.md],
 * правило 3). Списками, а не блоками: строки однородны, и блоки разрезали бы прокрутку
 * (CLAUDE.md, правило 3).
 */
const EMPTY_TEXT: Record<TasksTab, string> = {
  today: 'На сегодня задач нет',
  tomorrow: 'На завтра задач нет',
  upcoming: 'Впереди задач нет',
};

export function TasksScreen({
  state,
  actions,
  onOpenTask,
  onOpenCase,
  creating = false,
  onCreatingChange,
}: {
  state: TasksState;
  actions: TasksActions;
  onOpenTask: (id: string) => void;
  onOpenCase: (id: string) => void;
  creating?: boolean;
  onCreatingChange?: (creating: boolean) => void;
}) {
  const [menuFor, setMenuFor] = useState<TaskListItem | null>(null);
  const [deadlineFor, setDeadlineFor] = useState<TaskListItem | null>(null);

  return (
    <AppScreenScaffold
      topBar={<AppTopBar title="Задачи" root />}
      overlay={
        <>
          {menuFor ? (
            <AppMenuSheet
              title={menuFor.title}
              onDismiss={() => setMenuFor(null)}
              items={[
                {
                  icon: 'check',
                  label: menuFor.isCompleted ? 'Вернуть в работу' : 'Выполнено',
                  onSelect: () => void actions.setCompleted(menuFor.id, !menuFor.isCompleted),
                },
                {
                  icon: 'schedule',
                  label: 'Перенести срок',
                  onSelect: () => setDeadlineFor(menuFor),
                },
                ...(menuFor.caseId
                  ? [
                      {
                        icon: 'folder' as const,
                        label: 'Открыть дело',
                        onSelect: () => onOpenCase(menuFor.caseId ?? ''),
                      },
                    ]
                  : []),
              ]}
            />
          ) : null}

          {deadlineFor ? (
            <MoveDeadlineDialog
              task={deadlineFor}
              onDismiss={() => setDeadlineFor(null)}
              onConfirm={(at) => {
                void actions.setDeadline(deadlineFor.id, at);
                setDeadlineFor(null);
              }}
            />
          ) : null}

          {creating ? (
            <NewTaskDialog
              cases={state.cases}
              onDismiss={() => onCreatingChange?.(false)}
              onCreate={(title, deadline, caseId) => {
                void actions.createTask(title, deadline, caseId);
                onCreatingChange?.(false);
              }}
            />
          ) : null}
        </>
      }
    >
      <div className="tasks__tabs">
        {TABS.map((item) => (
          <AppChip
            key={item.key}
            label={
              // Число просроченного стоит прямо на вкладке: это то, ради чего экран
              // открывают, и прятать его внутрь списка нельзя.
              item.key === 'today' && state.overdueCount > 0
                ? `${item.label} · ${state.overdueCount}`
                : item.label
            }
            selected={state.tab === item.key}
            onClick={() => actions.setTab(item.key)}
          />
        ))}
      </div>

      {isEmptyTab(state.entries) && !state.isLoading ? (
        /*
         * Текст называет **вкладку**, а не пустоту вообще: «Задач нет» на «Завтра»
         * читается как «задач нет совсем», хотя на сегодня их может быть пять.
         * Кнопка — то, зачем сюда заходят с пустым списком.
         *
         * Говорится это и тогда, когда ниже висит серым заглядывание вперёд: иначе
         * завтрашние задачи под пустым «сегодня» прочтутся как сегодняшние.
         */
        <AppEmptyState
          icon="checklist"
          text={EMPTY_TEXT[state.tab] ?? 'Задач нет'}
          action={
            <AppButton variant="text" onClick={() => onCreatingChange?.(true)}>
              Поставить срок
            </AppButton>
          }
        />
      ) : null}

      <div className="tasks__list">
        {state.entries.map((entry) =>
          entry.kind === 'header' ? (
            <h2
              key={`header-${entry.title}`}
              className={`tasks__header ${entry.isOverdue ? 'tasks__header--overdue' : ''} ${
                entry.muted ? 'tasks__header--muted' : ''
              }`}
            >
              {entry.title}
              <span className="tasks__header-count">{entry.count}</span>
            </h2>
          ) : (
            <div key={entry.task.id} className={entry.muted ? 'tasks__peek' : undefined}>
              <AppSwipeRow
                start={{
                  icon: 'check',
                  label: entry.task.isCompleted ? 'Вернуть в работу' : 'Выполнено',
                  onAction: () =>
                    void actions.setCompleted(entry.task.id, !entry.task.isCompleted),
                }}
                end={{
                  icon: 'schedule',
                  label: 'Перенести',
                  onAction: () => setDeadlineFor(entry.task),
                }}
                onLongPress={() => setMenuFor(entry.task)}
              >
                <TaskRow
                  item={entry.task}
                  onOpen={() => onOpenTask(entry.task.id)}
                  onToggle={() => void actions.setCompleted(entry.task.id, !entry.task.isCompleted)}
                  onMenu={() => setMenuFor(entry.task)}
                />
              </AppSwipeRow>
              <AppDivider withIcon />
            </div>
          ),
        )}
      </div>
    </AppScreenScaffold>
  );
}

/** Перенос срока: дата и время, начиная с нынешнего срока задачи. */
function MoveDeadlineDialog({
  task,
  onDismiss,
  onConfirm,
}: {
  task: TaskListItem;
  onDismiss: () => void;
  onConfirm: (deadlineAt: number) => void;
}) {
  const current = new Date(task.deadlineAt);
  const [date, setDate] = useState(toDateValue(current));

  return (
    <AppFormDialog
      title="Перенести срок"
      onDismiss={onDismiss}
      confirm={{
        label: 'Готово',
        onClick: () => {
          const at = new Date(`${date}T${pad(current.getHours())}:${pad(current.getMinutes())}`);
          if (!Number.isNaN(at.getTime())) onConfirm(at.getTime());
        },
      }}
    >
      <AppDateField value={date} onChange={setDate} label={task.title} />
    </AppFormDialog>
  );
}

/**
 * Новая задача: название, срок и — по желанию — дело.
 *
 * Обязательное поле одно. Задачу ставят на ходу, и анкета здесь стоила бы того, что
 * её не поставят вовсе.
 */
function NewTaskDialog({
  cases,
  onDismiss,
  onCreate,
}: {
  cases: TaskCaseOption[];
  onDismiss: () => void;
  onCreate: (title: string, deadline: number, caseId: string | null) => void;
}) {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const [title, setTitle] = useState('');
  const [date, setDate] = useState(toDateValue(tomorrow));
  const [caseId, setCaseId] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);

  const chosen = cases.find((item) => item.id === caseId);

  return (
    <>
      <AppFormDialog
        title="Новая задача"
        onDismiss={onDismiss}
        confirm={{
          label: 'Создать',
          enabled: title.trim().length > 0,
          onClick: () => {
            // Полдень, а не полночь: срок «на завтра» без времени юрист понимает
            // как рабочий день, а не как ночь с сегодня на завтра.
            const at = new Date(`${date}T12:00`);
            if (!Number.isNaN(at.getTime())) onCreate(title, at.getTime(), caseId);
          },
        }}
      >
        <AppTextField value={title} onChange={setTitle} label="Что сделать" autoFocus />
        <AppDateField value={date} onChange={setDate} label="Срок" />
        <AppPickerRow
          label="Дело"
          value={chosen?.title ?? 'не выбрано'}
          onClick={() => setPicking(true)}
        />
      </AppFormDialog>

      {picking ? (
        <AppMenuSheet
          title="Дело задачи"
          onDismiss={() => setPicking(false)}
          items={[
            { icon: 'close', label: 'Без дела', onSelect: () => setCaseId(null) },
            ...cases.map((item) => ({
              icon: 'folder' as const,
              label: item.title,
              onSelect: () => setCaseId(item.id),
            })),
          ]}
        />
      ) : null}
    </>
  );
}

const pad = (value: number) => String(value).padStart(2, '0');

const toDateValue = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
